import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useField } from 'formik';

import GenericShippingMethod from './GenericShippingMethod';
import MondialRelayModal from './MondialRelay/MondialRelayModal';
import { MondialRelayAddress } from './MondialRelay/types';
import MondialRelayIcon from 'components/Common/Icons/Delivery/MondialRelay';
import Button from 'components/UI/Buttons/Button/Button';
import { MethodProps } from 'types/ShippingFormType';
import { MondialRelayPoint } from 'types/Cart';

import styles from './ShippingMethod.module.scss';

type Relay = MondialRelayPoint | undefined;

export default function MondialRelay({
  isSelected,
  method: { methodCode, estimateDeliveryDates },
  cart,
  setShippingInformation,
}: MethodProps): JSX.Element {
  const { t } = useTranslation(['checkout']);
  const [mondialRelayReady, setMondialRelayReady] = useState(false);
  const [mondialRelayOpen, setMondialRelayOpen] = useState(false);
  const [{ value: relaySelected }, , { setValue: setRelay }] = useField<Relay>(
    'mondialRelayPoint'
  );

  const currentSelected = useMemo<MondialRelayAddress | undefined>(() => {
    if (!relaySelected) {
      return undefined;
    }

    return {
      ID: relaySelected.id,
      Nom: relaySelected.name,
      Adresse1: relaySelected.street[0] || '',
      Adresse2: relaySelected.street[1] || '',
      CP: relaySelected.postalCode,
      Ville: relaySelected.city,
      Pays: relaySelected.countryCode,
      Lat: relaySelected.latitude,
      Long: relaySelected.longitude,
    };
  }, [relaySelected]);

  const onSelect = useCallback((mondialRelay: MondialRelayAddress) => {
    setMondialRelayOpen(false);

    const relay = {
      id: mondialRelay.ID,
      name: mondialRelay.Nom,
      street: [mondialRelay.Adresse1, mondialRelay.Adresse2],
      postalCode: mondialRelay.CP,
      city: mondialRelay.Ville,
      countryCode: mondialRelay.Pays,
      latitude: mondialRelay.Lat,
      longitude: mondialRelay.Long,
    };

    setRelay(relay);

    void setShippingInformation({
      shippingMethod: methodCode,
      mondialRelayPoint: relay,
    });
  }, []);

  useEffect(() => {
    if (isSelected) {
      void setShippingInformation({
        shippingMethod: methodCode,
        mondialRelayPoint: relaySelected,
      });
    }
  }, [isSelected]);

  return (
    <GenericShippingMethod
      value={methodCode}
      methodIcon={<MondialRelayIcon style={{ fontSize: '32px' }} />}
      methodLabel={
        <>
          <span className={styles.methodName}>
            {t('SHIPPING_MONDIAL_RELAY_METHOD_NAME')}
          </span>
          <span className={styles.methodNameExplain}>
            {t('SHIPPING_MONDIAL_RELAY_METHOD_NAME_EXPLAIN')}
          </span>
        </>
      }
      estimateDeliveryDates={estimateDeliveryDates}
      price={cart.shippingFees}
      freeFrom={
        cart.shippingFees
          ? cart.freeShippingThresholdInformation?.amount
          : undefined
      }
      classes={{
        methodContent: classNames(styles.relayContent, {
          [styles.isSelected]: isSelected,
        }),
      }}
    >
      {isSelected && (
        <>
          {relaySelected && <AddressRender address={relaySelected} />}
          <Button
            type={'button'}
            model={'primary'}
            disabled={!mondialRelayReady}
            onClick={() => setMondialRelayOpen(true)}
          >
            {t('SHIPPING_MONDIAL_RELAY_METHOD_CHOOSE_BUTTON')}
          </Button>
        </>
      )}
      <MondialRelayModal
        isOpen={mondialRelayOpen}
        onClose={() => setMondialRelayOpen(false)}
        widgetReady={() => setMondialRelayReady(true)}
        currentSelected={currentSelected}
        onSelect={onSelect}
      />
    </GenericShippingMethod>
  );
}

function AddressRender({ address }: { address: MondialRelayPoint }) {
  const { t } = useTranslation(['checkout']);

  return (
    <div className={styles.relayAddress}>
      <div className={styles.yourReplayPoint}>
        {t('SHIPPING_PICKUP_METHOD_YOUR_RELAY_POINT')}
      </div>
      <div className={styles.dataTextRelayPoint}>
        <div>{address.name}</div>
        <div>{address.street.join(' ')}</div>
        <div>{`${address.postalCode} ${address.city}`}</div>
      </div>
    </div>
  );
}
